import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { useDispatch } from 'react-redux';
import { addProductAsync, Product } from '../redux/slices/productSlice';
import * as mockDataService from '../services/mockData';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { AppDispatch } from '../redux/store';

interface ProductImporterProps {
  open: boolean;
  onClose: () => void;
}

interface ImportedProduct {
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  category: string;
  isValid: boolean;
  errors: string[];
}

const ProductImporter: React.FC<ProductImporterProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<string>('excel');
  const [loading, setLoading] = useState(false);
  const [importedProducts, setImportedProducts] = useState<ImportedProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [defaultCategory, setDefaultCategory] = useState('Outros');

  const steps = ['Selecionar arquivo', 'Revisar dados', 'Importar produtos'];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
      setError(null);
    }
  };

  const handleFileTypeChange = (event: SelectChangeEvent) => {
    setFileType(event.target.value);
  };

  const handleDefaultCategoryChange = (event: SelectChangeEvent) => {
    setDefaultCategory(event.target.value);
  };

  const parseCSV = (text: string): ImportedProduct[] => {
    const lines = text.split('\n');
    if (lines.length < 2) {
      throw new Error('O arquivo não contém dados suficientes');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const nameIndex = headers.indexOf('nome') !== -1 ? headers.indexOf('nome') : headers.indexOf('name');
    const descriptionIndex = headers.indexOf('descrição') !== -1 ? headers.indexOf('descrição') : headers.indexOf('description');
    const priceIndex = headers.indexOf('preço') !== -1 ? headers.indexOf('preço') : headers.indexOf('price');
    const imageUrlIndex = headers.indexOf('imagem') !== -1 ? headers.indexOf('imagem') : headers.indexOf('imageurl');
    const categoryIndex = headers.indexOf('categoria') !== -1 ? headers.indexOf('categoria') : headers.indexOf('category');

    if (nameIndex === -1 || descriptionIndex === -1 || priceIndex === -1) {
      throw new Error('O arquivo deve conter pelo menos as colunas Nome, Descrição e Preço');
    }

    const parsedProducts: ImportedProduct[] = [];

    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;

      const values = lines[i].split(',').map(v => v.trim());
      const product: ImportedProduct = {
        name: values[nameIndex] || '',
        description: values[descriptionIndex] || '',
        price: parseFloat(values[priceIndex]) || 0,
        imageUrl: imageUrlIndex !== -1 ? values[imageUrlIndex] || '' : '',
        category: categoryIndex !== -1 ? values[categoryIndex] || defaultCategory : defaultCategory,
        isValid: true,
        errors: [],
      };

      // Validação básica
      if (!product.name) {
        product.isValid = false;
        product.errors.push('Nome é obrigatório');
      }

      if (!product.description) {
        product.isValid = false;
        product.errors.push('Descrição é obrigatória');
      }

      if (isNaN(product.price) || product.price <= 0) {
        product.isValid = false;
        product.errors.push('Preço deve ser um número positivo');
      }

      parsedProducts.push(product);
    }

    return parsedProducts;
  };

  const parseExcel = async (file: File): Promise<ImportedProduct[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          if (!data) {
            reject(new Error('Erro ao ler o arquivo'));
            return;
          }
          
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Converter para JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (jsonData.length === 0) {
            reject(new Error('O arquivo não contém dados'));
            return;
          }
          
          // Mapear os dados para o formato de produtos
          const parsedProducts: ImportedProduct[] = jsonData.map((row: any) => {
            const product: ImportedProduct = {
              name: row.Nome || row.nome || row.Name || row.name || '',
              description: row.Descrição || row.descrição || row.Description || row.description || '',
              price: parseFloat(row.Preço || row.preço || row.Price || row.price || 0),
              imageUrl: row['URL da Imagem'] || row['url da imagem'] || row.ImageUrl || row.imageUrl || '',
              category: row.Categoria || row.categoria || row.Category || row.category || defaultCategory,
              isValid: true,
              errors: [],
            };
            
            // Validação básica
            if (!product.name) {
              product.isValid = false;
              product.errors.push('Nome é obrigatório');
            }
            
            if (!product.description) {
              product.isValid = false;
              product.errors.push('Descrição é obrigatória');
            }
            
            if (isNaN(product.price) || product.price <= 0) {
              product.isValid = false;
              product.errors.push('Preço deve ser um número positivo');
            }
            
            return product;
          });
          
          resolve(parsedProducts);
        } catch (error) {
          reject(new Error('Erro ao processar arquivo Excel'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Erro ao ler o arquivo'));
      };
      
      reader.readAsBinaryString(file);
    });
  };

  const handleProcessFile = async () => {
    if (!file) {
      setError('Por favor, selecione um arquivo');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let products: ImportedProduct[] = [];

      if (fileType === 'csv') {
        const text = await file.text();
        products = parseCSV(text);
      } else if (fileType === 'excel') {
        products = await parseExcel(file);
      } else {
        throw new Error('Formato de arquivo não suportado');
      }

      if (products.length === 0) {
        throw new Error('Nenhum produto encontrado no arquivo');
      }

      setImportedProducts(products);
      setActiveStep(1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const validProducts = importedProducts.filter(product => product.isValid);
      
      if (validProducts.length === 0) {
        throw new Error('Não há produtos válidos para importar');
      }

      // Processar os produtos em sequência para evitar problemas de concorrência
      for (const product of validProducts) {
        try {
          // Criar o objeto do produto com o tipo correto
          const newProduct: Product = {
            id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
            code: `P${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
            type: 'produto', // Especificando o tipo literal 'produto'
            name: product.name,
            description: product.description,
            price: product.price,
            imageUrl: product.imageUrl || 'https://via.placeholder.com/300x140?text=Produto',
            category: product.category,
          };
          
          // Usar o serviço mockData diretamente para salvar no localStorage
          // e depois atualizar o estado no Redux
          await mockDataService.addProduct(newProduct);
        } catch (productError) {
          console.error('Erro ao importar produto:', productError);
          // Continuar com o próximo produto mesmo se houver erro
        }
      }

      setSuccess(`${validProducts.length} produtos importados com sucesso!`);
      setActiveStep(2);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportedProducts([]);
    setError(null);
    setSuccess(null);
    setActiveStep(0);
    onClose();
  };

  const handleReset = () => {
    setFile(null);
    setImportedProducts([]);
    setError(null);
    setSuccess(null);
    setActiveStep(0);
  };

  const downloadTemplateCSV = () => {
    const headers = 'Nome,Descrição,Preço,URL da Imagem,Categoria\n';
    const exampleRow = 'Produto Exemplo,"Descrição detalhada do produto",99.90,https://exemplo.com/imagem.jpg,Categoria\n';
    const blob = new Blob([headers, exampleRow], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'template_produtos.csv');
  };

  const downloadTemplateExcel = () => {
    // Criar uma planilha
    const worksheet = XLSX.utils.json_to_sheet([
      {
        Nome: 'Produto Exemplo',
        Descrição: 'Descrição detalhada do produto',
        Preço: 99.90,
        'URL da Imagem': 'https://exemplo.com/imagem.jpg',
        Categoria: 'Categoria'
      }
    ]);
    
    // Criar um workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Produtos');
    
    // Gerar o arquivo
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'template_produtos.xlsx');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Importar Produtos</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Selecione o arquivo para importar
            </Typography>

            <FormControl fullWidth margin="normal">
              <InputLabel id="file-type-label">Tipo de Arquivo</InputLabel>
              <Select
                labelId="file-type-label"
                value={fileType}
                onChange={handleFileTypeChange}
                label="Tipo de Arquivo"
              >
                <MenuItem value="csv">CSV (.csv)</MenuItem>
                <MenuItem value="excel">Excel (.xls, .xlsx)</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                startIcon={<DownloadIcon />}
                onClick={fileType === 'csv' ? downloadTemplateCSV : downloadTemplateExcel}
                color="secondary"
              >
                Baixar Template
              </Button>
            </Box>

            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                mt: 2,
                mb: 3,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
              component="label"
            >
              <input
                type="file"
                accept={fileType === 'csv' ? '.csv' : '.xls,.xlsx'}
                hidden
                onChange={handleFileChange}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                Clique para selecionar ou arraste o arquivo aqui
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fileType === 'csv'
                  ? 'Arquivos CSV devem ter cabeçalhos: Nome, Descrição, Preço, URL da Imagem, Categoria'
                  : 'Suporta arquivos Excel (.xls, .xlsx)'}
              </Typography>
              {file && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  Arquivo selecionado: {file.name}
                </Typography>
              )}
            </Box>

            <Typography variant="h6" gutterBottom>
              Configurações padrão
            </Typography>

            <FormControl fullWidth margin="normal">
              <InputLabel id="default-category-label">Categoria Padrão</InputLabel>
              <Select
                labelId="default-category-label"
                value={defaultCategory}
                onChange={handleDefaultCategoryChange}
                label="Categoria Padrão"
              >
                <MenuItem value="Outros">Outros</MenuItem>
                <MenuItem value="Serviços">Serviços</MenuItem>
                <MenuItem value="Cursos">Cursos</MenuItem>
                <MenuItem value="Software">Software</MenuItem>
                <MenuItem value="Consultoria">Consultoria</MenuItem>
                <MenuItem value="E-books">E-books</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Revisar dados antes de importar
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Encontramos {importedProducts.length} produtos no arquivo. 
              {importedProducts.filter(p => !p.isValid).length > 0 && 
                ` (${importedProducts.filter(p => !p.isValid).length} com erros)`}
            </Typography>

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Preço</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Válido</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importedProducts.map((product, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        backgroundColor: product.isValid ? 'inherit' : 'error.light',
                      }}
                    >
                      <TableCell>{product.name}</TableCell>
                      <TableCell>
                        {product.description.length > 50
                          ? `${product.description.substring(0, 50)}...`
                          : product.description}
                      </TableCell>
                      <TableCell>R$ {product.price.toFixed(2)}</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell>
                        {product.isValid ? 'Sim' : (
                          <Typography color="error" variant="caption">
                            {product.errors.join(', ')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" gutterBottom>
              Importação concluída!
            </Typography>
            <Typography variant="body1" paragraph>
              {success}
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleReset}
              sx={{ mt: 2 }}
            >
              Importar mais produtos
            </Button>
          </Box>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {activeStep === 2 ? 'Fechar' : 'Cancelar'}
        </Button>
        {activeStep === 0 && (
          <Button 
            onClick={handleProcessFile} 
            variant="contained" 
            color="primary"
            disabled={!file || loading}
          >
            Processar Arquivo
          </Button>
        )}
        {activeStep === 1 && (
          <Button 
            onClick={handleImport} 
            variant="contained" 
            color="primary"
            disabled={loading || importedProducts.filter(p => p.isValid).length === 0}
          >
            Importar {importedProducts.filter(p => p.isValid).length} Produtos
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProductImporter; 